import React, { useRef, useEffect } from 'react'
import type { Column } from './utils'
import { useTar } from '@/modules'
import { MultiPick } from '@duck/ui'

interface TaskTableFiltersProps {
  filterText: string
  setFilterText: (text: string) => void
  difficultyFilter: string[]
  setDifficultyFilter: (difficulty: string[]) => void
  columns: Column[]
  columnDropdownOpen: boolean
  setColumnDropdownOpen: (open: boolean) => void
  toggleColumnVisibility: (columnKey: string) => void
}

export const TaskTableFilters: React.FC<TaskTableFiltersProps> = ({
  filterText,
  setFilterText,
  difficultyFilter,
  setDifficultyFilter,
  columns,
  columnDropdownOpen,
  setColumnDropdownOpen,
  toggleColumnVisibility,
}) => {
  const { t } = useTar()
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setColumnDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [setColumnDropdownOpen])

  return (
    <div className="bg-card shadow rounded-md sm:rounded-lg border border-border p-6">
      <div className="flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:space-x-4 lg:items-end">
        {/* Search Filter */}
        <div className="flex-1">
          <label htmlFor="search" className="block text-sm font-medium text-foreground mb-2">
            {t('common.textSearch')}
          </label>
          <input
            type="text"
            id="search"
            className="block w-full rounded-md border-border shadow-sm focus:border-accent focus:ring-accent sm:text-sm px-3 py-2.5 bg-card text-foreground"
            placeholder={t('common.searchByNameOrDescription')}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        {/* Difficulty Filter */}
        <div className="min-w-48">
          <label htmlFor="difficulty" className="block text-sm font-medium text-foreground mb-2">
            {t('common.difficulty')}
          </label>
          <MultiPick
            options={[
              { value: 'Easy', label: t('tasks.difficulty.easy') },
              { value: 'Intermediate', label: t('tasks.difficulty.intermediate') },
              { value: 'Hard', label: t('tasks.difficulty.hard') },
              { value: 'Expert', label: t('tasks.difficulty.expert') },
            ]}
            selected={difficultyFilter}
            onChange={(next) => setDifficultyFilter(next)}
            placeholder={t('common.allDifficulties')}
            className="py-[6px]"
          />
        </div>

        {/* Column Visibility Multi-Select */}
        <div className="relative min-w-48" ref={dropdownRef}>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('common.visibleColumns')}
          </label>
          <MultiPick
            options={columns.map(col => ({ value: col.key, label: t(col.label) }))}
            selected={columns.filter(c => c.visible).map(c => c.key)}
            onChange={(nextSelected) => {
              const selectedSet = new Set(nextSelected)
              columns.forEach(col => {
                const shouldBeVisible = selectedSet.has(col.key)
                if (shouldBeVisible !== col.visible) {
                  toggleColumnVisibility(col.key)
                }
              })
            }}
            placeholder={`${columns.filter(col => col.visible).length} / ${columns.length} ${t('common.columns')}`}
            className="py-[6px]"
          />
        </div>
      </div>
    </div>
  )
}
